import { createSlice } from '@reduxjs/toolkit';
import { API } from 'api';

const initialState = {
	requestQueue: [],
	requestHistory: {
		rows: [],
		count: 0,
		marked_count: 0,
		unmarked_count: 0,
	},
	paymentQueue: [],
	paymentHistory: [],
	loading: {
		requestQueue: false,
		requestHistory: false,
		paymentQueue: false,
		paymentHistory: false,
	},
	error: {
		requestQueue: null,
		requestHistory: null,
		paymentQueue: null,
		paymentHistory: null,
	},
};

export const financeSlice = createSlice({
	name: 'finance',
	initialState,
	reducers: {
		setRequestQueue: (state, action) => {
			state.requestQueue = action.payload;
			state.loading.requestQueue = false;
			state.error.requestQueue = null;
		},
		setRequestHistory: (state, action) => {
			state.requestHistory = {
				rows: action.payload.rows || [],
				count: action.payload.count || 0,
				marked_count: action.payload.marked_count || 0,
				unmarked_count: action.payload.unmarked_count || 0,
			};
			state.loading.requestHistory = false;
			state.error.requestHistory = null;
		},
		setPaymentQueue: (state, action) => {
			state.paymentQueue = action.payload;
			state.loading.paymentQueue = false;
			state.error.paymentQueue = null;
		},
		setPaymentHistory: (state, action) => {
			state.paymentHistory = action.payload;
			state.loading.paymentHistory = false;
			state.error.paymentHistory = null;
		},
		setLoading: (state, action) => {
			const { tab, loading } = action.payload;
			state.loading[tab] = loading;
		},
		setError: (state, action) => {
			const { tab, error } = action.payload;
			state.error[tab] = error;
			state.loading[tab] = false;
		},
	},
});

export const {
	setRequestQueue,
	setRequestHistory,
	setPaymentQueue,
	setPaymentHistory,
	setLoading,
	setError,
} = financeSlice.actions;

// Helper function to build filterBy object
const buildFilterBy = ({
	query_str,
	city,
	client_id,
	project_id,
	vendor_id,
	business_unit_ids,
	from_date,
	to_date,
	...rest
}) => {
	const filterBy = {};

	if (query_str) filterBy.query_str = query_str;
	if (city) filterBy.city = city;
	if (client_id) filterBy.client_id = client_id;
	if (project_id) filterBy.project_id = project_id;
	if (vendor_id) filterBy.vendor_id = vendor_id;
	if (business_unit_ids && business_unit_ids.length > 0)
		filterBy.business_unit_ids = business_unit_ids;
	if (from_date) filterBy.from_date = from_date;
	if (to_date) filterBy.to_date = to_date;

	return { ...filterBy, ...rest };
};

// Async thunk for fetching request queue
export const fetchRequestQueue =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'requestQueue', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'requests_queue',
						...filterBy,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setRequestQueue({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'requestQueue',
					error: error.message || 'Failed to fetch request queue',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching request history
export const fetchRequestHistory =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		unmarked_only,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'requestHistory', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'request_history',
						...(unmarked_only ? { unmarked_only: true } : {}),
						...filterBy,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setRequestHistory({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
					marked_count: response?.meta?.marked_count || 0,
					unmarked_count: response?.meta?.unmarked_count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
				marked_count: response?.meta?.marked_count || 0,
				unmarked_count: response?.meta?.unmarked_count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'requestHistory',
					error: error.message || 'Failed to fetch request history',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching payment queue
export const fetchPaymentQueue =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'paymentQueue', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'payments_queue',
						...filterBy,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setPaymentQueue({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'paymentQueue',
					error: error.message || 'Failed to fetch payment queue',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching payment history
export const fetchPaymentHistory =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'paymentHistory', loading: true }));
		try {
			const filterBy = buildFilterBy({
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			});

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'finance_request',
					offset,
					limit,
					filter_by: {
						tab: 'payments_history',
						...filterBy,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setPaymentHistory({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'paymentHistory',
					error: error.message || 'Failed to fetch payment history',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching finance request status history
export const fetchFinanceRequestStatusHistory =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request_status_history',
				filter_by: {
					finance_request_id,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			data: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

// Async thunk for fetching a single finance request detail
export const fetchFinanceRequestDetail =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request',
				filter_by: {
					id,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			rows: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

// Async thunk for fetching finance request navigation (previous/next)
export const fetchFinanceRequestNavigation =
	({ current_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'finance_request_navigation',
				filter_by: {
					current_request_id,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			previous_id: response?.data?.previous_request_id || null,
			next_id: response?.data?.next_request_id || null,
		};
	};

// Async thunk for fetching pro-forma invoice (for viewing)
export const fetchProformaInvoice =
	({ finance_request_id, url_type = 'view' }) =>
	async () => {
		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'proforma_invoice',
				file_type: 'pdf',
				data: {
					id: finance_request_id,
					url_type,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			pdf_url: response?.data?.pdf_url || null,
			proforma_invoice_number: response?.data?.proforma_invoice_number || null,
			is_supervisor_uploaded: response?.data?.is_supervisor_uploaded || false,
			view_url: response?.data?.view_url || response?.data?.signed_url || null,
			document_url:
				response?.data?.view_url ||
				response?.data?.signed_url ||
				response?.data?.pdf_url ||
				response?.data?.download_url ||
				null,
			download_url: response?.data?.download_url || response?.data?.pdf_url || null,
			invoice_data: response?.data || null,
		};
	};

// Async thunk for generating pro-forma invoice
export const generateProformaInvoice =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'generate_proforma_invoice',
				data: {
					id: finance_request_id,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			pdf_url: response?.data?.pdf_url || null,
			proforma_invoice_number: response?.data?.proforma_invoice_number || null,
			is_supervisor_uploaded: response?.data?.is_supervisor_uploaded || false,
			document_url: response?.data?.pdf_url || null,
			download_url: response?.data?.pdf_url || null,
			invoice_number: response?.data?.proforma_invoice_number || null,
		};
	};

// Async thunk for downloading pro-forma invoice
export const downloadProformaInvoice =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'proforma_invoice',
				file_type: 'pdf',
				data: {
					id: finance_request_id,
					url_type: 'download',
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			download_url: response?.data?.download_url || null,
		};
	};

// Async thunk for approving payment
export const approvePayment =
	({ finance_request_id, paid_date, utr_number, paid_amount, login_user_id }) =>
	async (dispatch, getState) => {
		// Get login_user_id from auth state if not provided
		const state = getState();
		const userId = login_user_id || state?.auth?.me?.login_user_id || null;

		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					id: finance_request_id,
					utr_number,
					paid_date,
					paid_amount,
					payment_status: 'Completed',
					...(userId && { login_user_id: userId }),
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

// Async thunk for rejecting payment
export const rejectPayment =
	({ finance_request_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'finance_request',
				data: {
					id: finance_request_id,
					status: 'Unmarked',
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return response?.data || null;
	};

// Helper function to upload file to S3
const uploadFileToS3 = async (url, file, contentType) => {
	const response = await fetch(url, {
		method: 'PUT',
		headers: { 'Content-Type': contentType },
		body: file,
	});

	if (!response.ok) {
		throw new Error(`S3 upload failed: HTTP ${response.status}`);
	}

	return { success: true };
};

// Async thunk for uploading pro-forma invoice
export const uploadProformaInvoice =
	({ file, id }) =>
	async () => {
		const signedUrlCredentials = {
			url: '/get-signed-url',
			data: {
				url_type: 'upload',
				data: {
					entity: 'trip',
					keys: [file.name],
				},
			},
		};

		const signedUrlResponse = await API.common(signedUrlCredentials);
		const fileData = signedUrlResponse?.data?.[0];

		if (!fileData?.url || !fileData?.filename) {
			throw new Error('Failed to get signed URL from server');
		}

		await uploadFileToS3(fileData.url, file, file.type);

		const updateCredentials = {
			url: '/update',
			data: {
				update_type: 'upload_proforma_invoice',
				data: {
					id,
					key: fileData.filename,
				},
			},
		};

		const response = await API.common(updateCredentials);
		return response?.data || response || null;
	};

export default financeSlice.reducer;
